import React from 'react';
import { Link } from 'react-router-dom';
import {
  Gift,
  Sparkles,
  Zap,
  Trophy,
  ArrowRight,
  Share2,
  Users,
  Check,
  Shield,
  Heart,
  Crown,
  Rocket,
  Star,
  TrendingUp,
  BadgeCheck,
  Smartphone,
} from 'lucide-react';
import AltHeader from '../components/AltHeader';
import AltFooter from '../components/AltFooter';
import AltParallaxBackdrop from '../components/AltParallaxBackdrop';

/* ─────────── DATA ─────────── */

const HOW_IT_WORKS = [
  {
    n: '01',
    Icon: Share2,
    t: 'Grab your link',
    b: 'Every Levanta member has a unique personal referral link inside the in-app Referral Hub.',
  },
  {
    n: '02',
    Icon: Users,
    t: 'Share it with friends',
    b: 'Send it in messages, post it on social, say it out loud — however feels natural.',
  },
  {
    n: '03',
    Icon: Gift,
    t: 'Earn automatically',
    b: 'The moment a friend joins and starts an active paid subscription, rewards flow to your account.',
  },
];

const REWARD_LAYERS = [
  {
    label: 'Layer 1',
    Icon: Trophy,
    title: 'Milestone badges',
    body: 'Permanent status markers you earn based on the total friends you have ever referred. Kept forever — even if friends later cancel.',
    bg: 'from-amber-400/20 to-orange-500/10',
    iconColor: 'text-amber-600',
    iconBg: 'from-amber-400 to-orange-500',
  },
  {
    label: 'Layer 2',
    Icon: Zap,
    title: 'XP — experience points',
    body: '50 XP per active friend, every month. Flows automatically on the 1st. Already-earned XP stays yours — only future XP from that friend stops if they leave.',
    bg: 'from-blue-400/20 to-cyan-500/10',
    iconColor: 'text-blue-600',
    iconBg: 'from-blue-500 to-cyan-400',
  },
  {
    label: 'Layer 3',
    Icon: Gift,
    title: 'Free membership',
    body: 'Have 4 or more active paying friends when billing runs? Your membership is free that month. Dynamic — every cycle recalculates based on who is still active.',
    bg: 'from-emerald-400/20 to-green-500/10',
    iconColor: 'text-emerald-600',
    iconBg: 'from-emerald-500 to-green-500',
  },
];

const BADGES = [
  { threshold: 1, name: 'Connector', Icon: Heart, tint: 'from-rose-500 to-pink-500' },
  { threshold: 3, name: 'Builder', Icon: Rocket, tint: 'from-orange-500 to-amber-500' },
  { threshold: 5, name: 'Tribe Starter', Icon: Users, tint: 'from-amber-500 to-yellow-500' },
  { threshold: 10, name: 'Tribe Leader', Icon: Star, tint: 'from-sky-500 to-blue-500' },
  { threshold: 20, name: 'Growth Ambassador', Icon: TrendingUp, tint: 'from-violet-500 to-purple-500' },
  { threshold: 50, name: 'Levanta Legend', Icon: Crown, tint: 'from-emerald-500 to-teal-500' },
];

const XP_TABLE = [
  { m: 1, active: 1, thisMonth: 50, total: 50, note: 'First active friend' },
  { m: 3, active: 3, thisMonth: 150, total: 300, note: 'Momentum building' },
  { m: 6, active: 6, thisMonth: 300, total: 1050, note: 'Serious progression' },
  { m: 10, active: 10, thisMonth: 500, total: 3300, note: 'Compounding hard' },
  { m: 12, active: 10, thisMonth: 500, total: 4300, note: 'One year in' },
];

const FREE_TIERS = [
  { friends: '0 – 3', pay: '$10 / month', status: 'Regular membership' },
  { friends: '4', pay: '$0', status: 'Free — exactly at threshold' },
  { friends: '5 – 9', pay: '$0', status: 'Free + buffer against cancellations' },
  { friends: '10+', pay: '$0', status: 'Free + strong buffer' },
];

const SCENARIOS = [
  {
    title: 'You have 5 active friends — 1 cancels',
    body: 'You drop from 5 to 4 active friends. Your membership stays free. The cancellation had zero cost impact because you had a buffer.',
  },
  {
    title: 'You have 4 active friends — 1 cancels',
    body: 'You drop from 4 to 3. At the next billing cycle you pay $10. The moment you bring one new friend who activates, you are back to 4 — and free the following cycle.',
  },
  {
    title: 'You have 10 active friends — 3 cancel',
    body: 'You drop from 10 to 7. Still free. Having more active friends gives you a bigger cushion against churn.',
  },
  {
    title: 'You have 3 friends — then bring 1 more',
    body: 'The moment that 4th friend\u2019s first payment goes through, you qualify for free membership at the next billing cycle. Your Tribe Provider badge also activates on your profile.',
  },
];

const RULES = [
  'Only friends who join through your unique referral link and are on an active paid subscription count towards rewards.',
  'Free trials and inactive accounts do not count.',
  'XP and badges are awarded automatically — no approval, no waiting.',
  'Self-referrals are not permitted (a second account to refer yourself will be removed).',
  'If a friend cancels, you keep all XP and badges you have already earned. Only future rewards from that friend stop.',
  'Free-membership qualification recalculates every billing cycle based on current active friends.',
];

/* ─────────── PAGE ─────────── */

const BringAFriend = () => {
  return (
    <div className="bg-white overflow-x-hidden">
      <AltHeader />

      {/* ───── HERO ───── */}
      <section className="relative pt-[110px] md:pt-[170px] pb-16 md:pb-28 px-6 overflow-hidden min-h-[640px] md:min-h-[820px]">
        <AltParallaxBackdrop />
        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <span
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#0b1f3a]/80 backdrop-blur-md text-xs md:text-[13px] font-semibold tracking-[0.18em] uppercase text-white shadow-lg ring-1 ring-white/20 mb-7"
            data-testid="baf-eyebrow"
          >
            <Gift className="w-3.5 h-3.5 text-cyan-300" strokeWidth={2.5} />
            Member Referral Program
          </span>
          <h1
            className="text-4xl md:text-7xl font-black leading-[1.02] tracking-tight text-[#0b1f3a] alt-hero-glow"
            data-testid="baf-hero-title"
          >
            Invite friends. Earn XP.{' '}
            <em className="italic font-light text-blue-600">Pay nothing.</em>
          </h1>
          <p className="mt-8 text-lg md:text-xl text-[#0b1f3a]/75 max-w-2xl mx-auto leading-relaxed alt-hero-glow">
            The Levanta journey grows better with people. Share it with four
            friends who join — and your membership becomes free, forever, for
            as long as they grow with you.
          </p>

          {/* Headline stat row */}
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 max-w-4xl mx-auto">
            {[
              { big: '50', sub: 'XP per active friend, every month' },
              { big: '4', sub: 'Active friends = free membership' },
              { big: '6', sub: 'Milestone badges to collect' },
              { big: '$0', sub: 'Your membership when 4+ stay active' },
            ].map((s) => (
              <div
                key={s.sub}
                className="bg-white/85 backdrop-blur-sm rounded-2xl ring-1 ring-black/5 shadow-sm px-5 py-5 text-center"
              >
                <p className="text-3xl md:text-4xl font-black text-blue-600 tracking-tight">
                  {s.big}
                </p>
                <p className="mt-1.5 text-[12px] md:text-[13px] text-[#0b1f3a]/70 leading-snug font-medium">
                  {s.sub}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── HOW IT WORKS ───── */}
      <section className="px-6 py-20 md:py-28 bg-white">
        <div className="max-w-6xl mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-4 text-center">
            How it works
          </p>
          <h2 className="text-3xl md:text-5xl font-black leading-[1.05] tracking-tight text-[#0b1f3a] text-center mb-14">
            Three steps.{' '}
            <em className="italic font-light text-blue-600">Automatic after that.</em>
          </h2>
          <div className="grid md:grid-cols-3 gap-5">
            {HOW_IT_WORKS.map(({ n, Icon, t, b }) => (
              <div
                key={n}
                className="rounded-3xl bg-white ring-1 ring-black/5 shadow-sm p-8 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
              >
                <p className="text-[11px] font-mono tracking-[0.2em] text-blue-600/70 mb-5">
                  STEP {n}
                </p>
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-400 text-white flex items-center justify-center mb-5 shadow-md">
                  <Icon className="w-6 h-6" strokeWidth={2} />
                </div>
                <h3 className="text-xl md:text-2xl font-black text-[#0b1f3a] leading-tight mb-3">
                  {t}
                </h3>
                <p className="text-[#0b1f3a]/70 leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── 3 REWARD LAYERS ───── */}
      <section className="px-6 py-24 md:py-28 bg-[#f4f8fc]">
        <div className="max-w-6xl mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-4 text-center">
            The three reward layers
          </p>
          <h2 className="text-3xl md:text-5xl font-black leading-[1.05] tracking-tight text-[#0b1f3a] text-center mb-14">
            Every referral pays you in{' '}
            <em className="italic font-light text-blue-600">three different ways.</em>
          </h2>
          <div className="grid md:grid-cols-3 gap-5">
            {REWARD_LAYERS.map((l) => (
              <div
                key={l.title}
                className={`relative rounded-3xl bg-gradient-to-br ${l.bg} ring-1 ring-black/5 shadow-sm p-8 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 overflow-hidden`}
              >
                <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/40 rounded-full blur-3xl" />
                <div className="relative">
                  <p className="text-[11px] font-mono tracking-[0.22em] text-[#0b1f3a]/50 mb-5">
                    {l.label.toUpperCase()}
                  </p>
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${l.iconBg} text-white flex items-center justify-center mb-5 shadow-lg`}>
                    <l.Icon className="w-6 h-6" strokeWidth={2} />
                  </div>
                  <h3 className="text-xl md:text-2xl font-black text-[#0b1f3a] leading-tight mb-3">
                    {l.title}
                  </h3>
                  <p className="text-[#0b1f3a]/75 leading-relaxed">{l.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── LAYER 1 · BADGES ───── */}
      <section className="px-6 py-24 md:py-28 bg-white">
        <div className="max-w-6xl mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-600 mb-4 text-center">
            Layer 01 · Milestone badges
          </p>
          <h2 className="text-3xl md:text-5xl font-black leading-[1.05] tracking-tight text-[#0b1f3a] text-center mb-6">
            Six badges to earn.{' '}
            <em className="italic font-light text-blue-600">Kept forever.</em>
          </h2>
          <p className="text-center text-[#0b1f3a]/65 max-w-2xl mx-auto mb-14 leading-relaxed">
            Triggered by total lifetime referrals — not current ones. Once
            earned, a badge is yours to keep even if every one of those friends
            leaves a year later.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
            {BADGES.map(({ threshold, name, Icon, tint }) => (
              <div
                key={name}
                className="group rounded-3xl bg-[#f4f8fc] ring-1 ring-black/5 p-7 text-center hover:-translate-y-1 hover:shadow-lg hover:bg-white transition-all duration-300"
              >
                <div
                  className={`w-20 h-20 mx-auto rounded-full bg-gradient-to-br ${tint} text-white flex items-center justify-center shadow-lg mb-5 ring-4 ring-white`}
                >
                  <Icon className="w-9 h-9" strokeWidth={1.8} />
                </div>
                <p className="text-[11px] font-mono tracking-[0.22em] text-[#0b1f3a]/50 mb-1.5">
                  {threshold} {threshold === 1 ? 'friend' : 'friends'}
                </p>
                <p className="text-lg md:text-xl font-black text-[#0b1f3a]">
                  {name}
                </p>
              </div>
            ))}
          </div>

          {/* Tribe Provider — dynamic badge */}
          <div className="mt-10 rounded-3xl bg-gradient-to-br from-emerald-50 via-white to-teal-50 ring-1 ring-emerald-100 p-8 md:p-10 flex flex-col md:flex-row items-center gap-6">
            <div className="w-20 h-20 shrink-0 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 text-white flex items-center justify-center shadow-lg ring-4 ring-white">
              <BadgeCheck className="w-9 h-9" strokeWidth={1.8} />
            </div>
            <div className="flex-1">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-700 mb-2">
                Bonus · Dynamic badge
              </p>
              <h3 className="text-2xl md:text-3xl font-black text-[#0b1f3a] leading-tight mb-2">
                Tribe Provider
              </h3>
              <p className="text-[#0b1f3a]/70 leading-relaxed">
                Appears on your profile whenever you have <strong>4 or more
                active paying friends</strong>. Unlike the milestone badges,
                this one reflects your current status — it turns on and off
                with your active-friend count. It is the visual signal that
                your membership is free right now.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ───── LAYER 2 · XP ───── */}
      <section className="px-6 py-24 md:py-28 bg-[#0b1f3a] text-white">
        <div className="max-w-6xl mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-300 mb-4 text-center">
            Layer 02 · XP
          </p>
          <h2 className="text-3xl md:text-5xl font-black leading-[1.05] tracking-tight text-center mb-6">
            50 XP per active friend.{' '}
            <em className="italic font-light text-blue-300">Every month.</em>
          </h2>
          <p className="text-center text-white/70 max-w-2xl mx-auto mb-14 leading-relaxed">
            XP feeds directly into your Levanta progression — it levels you up
            inside the system. Referred-friend XP flows in automatically on the
            1st of each month based on who is still active.
          </p>

          <div className="overflow-x-auto rounded-3xl ring-1 ring-white/10 bg-white/5 backdrop-blur-sm">
            <table className="w-full min-w-[680px] text-sm md:text-[15px]">
              <thead>
                <tr className="text-white/80 border-b border-white/10">
                  <th className="px-4 py-4 text-left font-semibold">Month</th>
                  <th className="px-4 py-4 text-center font-semibold">Active friends</th>
                  <th className="px-4 py-4 text-right font-semibold">XP this month</th>
                  <th className="px-4 py-4 text-right font-semibold text-blue-300">Total XP</th>
                  <th className="px-4 py-4 text-left font-semibold">Notes</th>
                </tr>
              </thead>
              <tbody>
                {XP_TABLE.map((r) => (
                  <tr key={r.m} className="border-t border-white/5 text-white/75">
                    <td className="px-4 py-3.5 font-mono text-white/60">Month {r.m}</td>
                    <td className="px-4 py-3.5 text-center">{r.active}</td>
                    <td className="px-4 py-3.5 text-right">+{r.thisMonth} XP</td>
                    <td className="px-4 py-3.5 text-right font-bold text-blue-300">
                      {r.total.toLocaleString()}
                    </td>
                    <td className="px-4 py-3.5 text-white/55">{r.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-8 rounded-2xl bg-white/5 ring-1 ring-white/10 p-6 max-w-3xl mx-auto flex items-start gap-4">
            <Zap className="w-6 h-6 text-cyan-300 shrink-0 mt-0.5" />
            <p className="text-white/75 leading-relaxed text-[15px]">
              <strong className="text-white">Formula:</strong> 50 XP × active
              friends × months active. Ten active friends for ten straight
              months is 5,000 XP — and that is on top of everything you earn
              from your own practice inside the system.
            </p>
          </div>
        </div>
      </section>

      {/* ───── LAYER 3 · FREE MEMBERSHIP ───── */}
      <section className="px-6 py-24 md:py-28 bg-gradient-to-b from-[#eaf3fb] to-white">
        <div className="max-w-6xl mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-700 mb-4 text-center">
            Layer 03 · Free membership
          </p>
          <h2 className="text-3xl md:text-5xl font-black leading-[1.05] tracking-tight text-[#0b1f3a] text-center mb-6">
            Invite 4.{' '}
            <em className="italic font-light text-blue-600">Pay no more.</em>
          </h2>
          <p className="text-center text-[#0b1f3a]/65 max-w-2xl mx-auto mb-14 leading-relaxed">
            This is the big one. Have 4 or more friends on an active paid
            subscription when your billing cycle runs, and Levanta is free
            that month. Dynamic — recalculated every cycle.
          </p>

          <div className="overflow-x-auto rounded-3xl ring-1 ring-black/5 shadow-sm bg-white">
            <table className="w-full min-w-[600px] text-sm md:text-[15px]">
              <thead>
                <tr className="bg-[#f4f8fc] text-[#0b1f3a]">
                  <th className="px-4 py-4 text-left font-semibold">Active friends this month</th>
                  <th className="px-4 py-4 text-center font-semibold">You pay</th>
                  <th className="px-4 py-4 text-left font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {FREE_TIERS.map((t, i) => (
                  <tr
                    key={t.friends}
                    className={`border-t border-black/5 ${
                      i >= 1 ? 'bg-emerald-50/40' : ''
                    }`}
                  >
                    <td className="px-4 py-4 font-mono text-[#0b1f3a]/75">{t.friends}</td>
                    <td className={`px-4 py-4 text-center font-black text-lg ${
                      i >= 1 ? 'text-emerald-600' : 'text-[#0b1f3a]'
                    }`}>
                      {t.pay}
                    </td>
                    <td className="px-4 py-4 text-[#0b1f3a]/70">{t.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Real-world scenarios */}
          <div className="mt-16">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-4 text-center">
              Real-world scenarios
            </p>
            <h3 className="text-2xl md:text-4xl font-black text-[#0b1f3a] text-center mb-10 leading-[1.1]">
              What actually happens when{' '}
              <em className="italic font-light text-blue-600">friends churn.</em>
            </h3>
            <div className="grid md:grid-cols-2 gap-5">
              {SCENARIOS.map((s, i) => (
                <div
                  key={s.title}
                  className="rounded-3xl bg-white ring-1 ring-black/5 shadow-sm p-7 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
                >
                  <p className="font-mono text-[11px] tracking-[0.22em] text-blue-600/70 mb-3">
                    SCENARIO {String(i + 1).padStart(2, '0')}
                  </p>
                  <h4 className="text-lg md:text-xl font-black text-[#0b1f3a] mb-3 leading-snug">
                    {s.title}
                  </h4>
                  <p className="text-[#0b1f3a]/70 leading-relaxed">{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ───── COMPLETE REWARD SUMMARY ───── */}
      <section className="px-6 py-24 md:py-28 bg-white">
        <div className="max-w-5xl mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-4 text-center">
            The whole program on one card
          </p>
          <h2 className="text-3xl md:text-5xl font-black leading-[1.05] tracking-tight text-[#0b1f3a] text-center mb-14">
            Complete reward{' '}
            <em className="italic font-light text-blue-600">summary.</em>
          </h2>

          <div className="overflow-x-auto rounded-3xl ring-1 ring-black/5 shadow-sm">
            <table className="w-full min-w-[720px] text-sm md:text-[15px]">
              <thead>
                <tr className="bg-[#0b1f3a] text-white">
                  <th className="px-4 py-4 text-left font-semibold">Reward</th>
                  <th className="px-4 py-4 text-left font-semibold">Trigger</th>
                  <th className="px-4 py-4 text-center font-semibold">Permanent?</th>
                  <th className="px-4 py-4 text-left font-semibold">Value</th>
                </tr>
              </thead>
              <tbody className="text-[#0b1f3a]/80">
                {[
                  ['Milestone badges', '1, 3, 5, 10, 20, 50 total friends', 'Yes — kept forever', 'Status + identity'],
                  ['XP', '50 per active friend per month', 'Yes — earned XP stays', 'Progression + levels'],
                  ['Tribe Provider badge', '4+ active paying friends right now', 'Dynamic — reflects current status', 'Status signal'],
                  ['Free membership', '4+ active paying friends at billing', 'Dynamic — every cycle', '$10 saved per month'],
                ].map((row) => (
                  <tr key={row[0]} className="border-t border-black/5 hover:bg-[#fafcff]">
                    <td className="px-4 py-4 font-bold text-[#0b1f3a]">{row[0]}</td>
                    <td className="px-4 py-4">{row[1]}</td>
                    <td className="px-4 py-4 text-center">{row[2]}</td>
                    <td className="px-4 py-4 text-blue-700 font-semibold">{row[3]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ───── RULES ───── */}
      <section className="px-6 py-24 md:py-28 bg-[#f4f8fc]">
        <div className="max-w-4xl mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-4 text-center">
            The rules
          </p>
          <h2 className="text-3xl md:text-5xl font-black leading-[1.05] tracking-tight text-[#0b1f3a] text-center mb-12">
            Clear.{' '}
            <em className="italic font-light text-blue-600">No fine print.</em>
          </h2>
          <div className="rounded-3xl bg-white ring-1 ring-black/5 shadow-sm p-8 md:p-10">
            <ul className="space-y-5">
              {RULES.map((r) => (
                <li key={r} className="flex items-start gap-4">
                  <div className="shrink-0 w-6 h-6 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mt-0.5">
                    <Check className="w-3.5 h-3.5" strokeWidth={3} />
                  </div>
                  <span className="text-[#0b1f3a]/80 leading-relaxed text-[17px]">{r}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-6 flex items-start gap-3 bg-amber-50 ring-1 ring-amber-200 rounded-2xl p-5 max-w-3xl mx-auto">
            <Shield className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-[#7a5a12] leading-relaxed text-sm">
              <strong>Anti-abuse:</strong> self-referrals via a second account
              are not permitted. Levanta reserves the right to remove rewards
              and suspend accounts found circumventing these rules.
            </p>
          </div>
        </div>
      </section>

      {/* ───── FIND YOUR LINK ───── */}
      <section className="px-6 py-24 md:py-28 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-3xl bg-gradient-to-br from-[#0b1f3a] via-[#0f2a4d] to-[#0b1f3a] text-white p-10 md:p-14 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-60 h-60 bg-blue-500/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-cyan-400/10 rounded-full blur-3xl" />
            <div className="relative grid md:grid-cols-[auto_1fr] gap-8 items-center">
              <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center shadow-2xl">
                <Smartphone className="w-10 h-10" strokeWidth={1.8} />
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-300 mb-3">
                  Where to find your link
                </p>
                <h2 className="text-3xl md:text-4xl font-black leading-[1.05] tracking-tight mb-4">
                  Inside the Levanta app.{' '}
                  <em className="italic font-light text-blue-300">Referral Hub.</em>
                </h2>
                <p className="text-white/75 leading-relaxed mb-6 text-[17px]">
                  Every member has a unique personal link waiting for them in
                  the in-app Referral Hub. Tap, copy, share anywhere. Tracking
                  is automatic — you do nothing else.
                </p>
                <Link
                  to="/download-app"
                  data-testid="baf-download"
                  className="inline-flex items-center gap-2.5 md:gap-3 px-6 md:px-8 py-3.5 md:py-4 rounded-full bg-white text-[#0b1f3a] text-sm md:text-base font-semibold shadow-md hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-200 whitespace-nowrap"
                >
                  Open the Levanta app
                  <ArrowRight className="w-4 h-4 shrink-0" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───── CLOSING ───── */}
      <section className="px-6 py-24 md:py-28 bg-[#0b1f3a] text-white">
        <div className="max-w-3xl mx-auto text-center">
          <Sparkles className="w-8 h-8 text-cyan-300 mx-auto mb-6" strokeWidth={1.8} />
          <h2 className="text-4xl md:text-6xl font-black leading-[1.05] tracking-tight mb-6">
            Growth is better{' '}
            <em className="italic font-light text-blue-300">together.</em>
          </h2>
          <p className="text-white/70 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto">
            The people around you shape who you become. Bring the four people
            who want to grow with you — and Levanta becomes something the whole
            tribe is building, not something you are doing alone.
          </p>
        </div>
      </section>

      <AltFooter />
    </div>
  );
};

export default BringAFriend;
